import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, Injector, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { environment } from '@environments/environment';
import { authInterceptor } from '@shared/interceptors/auth-interceptor';
import { handleErrorInterceptor } from '@shared/interceptors/handle-error-Interceptor';
import { PwaService } from '@shared/service/other_services/pwa.service';
import { CookieService } from 'ngx-cookie-service';
import { catchError, of, take } from 'rxjs';
import { RouteReuseStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AppRoutes } from './app.routes';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { JALALI_MOMENT_FORMATS, MOMENT_FORMATS } from '@shared/helper/jalali_moment_formats';
import { JalaliMomentDateAdapter } from '@shared/adapters/material.persian-date.adapter';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { GoogleLoginProvider } from '@abacritt/angularx-social-login';
import { ImagePipe } from '@shared/pipes/image.pipe';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { LocalRepository } from '@shared/helper/local-repository';
import { EXPLORE_SCROLL_POSITION_KEY, FEED_SCROLL_POSITION_KEY, NOTIFICATION_SCROLL_POSITION_KEY } from 'constants/storage-keys';
import { setTheme } from './theme/themeing';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { CustomRouteReuseStrategy } from 'reuse-strategy/custom-route-reuse';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { ExceptionLogger } from './log/exeptionLogger';
import { JL } from 'jsnlog';
import { UserStateService } from './state/user-state.service';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { FCMService } from '@shared/service/firebase_services/fcm.service';


const initializer = () => {

  const injector = inject(Injector);
  const pwaService = injector.get(PwaService);
  const fcmService = injector.get(FCMService);
  const userStateService = inject(UserStateService);
  const repo = inject(LocalRepository);

  // ? logger config
  JL.setOptions({ defaultAjaxUrl: environment.ServicesUrl + '/log/jsnlog.logger' });
  JL().setOptions({ level: JL.getErrorLevel() });

  if (repo.IsInBrowser) {
    pwaService.StartPWA();
    fcmService.listenToFcmMessages().subscribe();
    repo.removeLocalItem(FEED_SCROLL_POSITION_KEY);
    repo.removeLocalItem(NOTIFICATION_SCROLL_POSITION_KEY);
    repo.removeLocalItem(EXPLORE_SCROLL_POSITION_KEY);
  }
  setTheme(repo);

  return userStateService.initializeUserState().pipe(take(1), catchError(() => of(null)));

}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initializer),
    provideRouter(AppRoutes),
    provideAnimations(),
    provideClientHydration(
      withHttpTransferCacheOptions({ includePostRequests: true, includeHeaders: ['cookie'], includeRequestsWithAuthHeaders: true }),
      withEventReplay()
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, handleErrorInterceptor])
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    provideServiceWorker('service-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    ImagePipe,
    CookieService,
    JwtHelperService,
    {
      provide: JWT_OPTIONS,
      useValue: {}
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy
    },
    {
      provide: ErrorHandler,
      useClass: ExceptionLogger
    },
    {
      provide: DateAdapter,
      useClass: JalaliMomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_LOCALE, useValue: "fa" },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (locale: any) => {
        if (locale === "fa") {
          return JALALI_MOMENT_FORMATS;
        } else {
          return MOMENT_FORMATS;
        }
      },
      deps: [MAT_DATE_LOCALE]
    },
    // ? google auth config
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider('48920246526-rf5uaor5hc2qprrsn2a4hbfv71p5fl0k.apps.googleusercontent.com'),
          },
        ],
      } as SocialAuthServiceConfig,
    }
  ]
};
